import { 
  encryptData, 
  decryptData, 
  isLikelyEncrypted 
} from './crypto';

const STORAGE_CONFIG = {
  prefix: 'secure_v2_', // Prefijo consistente con CreateWallet, Login, y Dashboard
  encryptionFlag: '__enc__',
  maxSize: 1024 * 1024 * 4.5, // 4.5MB (límite práctico para localStorage)
};

/**
 * Guarda datos con control de tamaño y estructura mejorada
 * Nota: sessionActive se almacena como string ('true'/'false') sin payload para compatibilidad con CreateWallet, Login, y Dashboard
 * Nota: Considera usar secure-ls para encriptar datos en localStorage en producción
 */
export async function saveToStorage(key, value, encryptionKey = null) {
  try {
    // Validaciones estrictas
    if (!key || typeof key !== 'string') {
      throw new Error(`La clave debe ser un string no vacío (recibido: ${typeof key})`);
    }

    if (value === undefined) {
      throw new Error('No se puede almacenar undefined');
    }

    const storageKey = STORAGE_CONFIG.prefix + key;

    // Caso especial para sessionActive: almacenar como string directamente
    if (key === 'sessionActive') {
      if (value !== 'true' && value !== 'false') {
        throw new Error('sessionActive debe ser "true" o "false"');
      }
      localStorage.setItem(storageKey, value);
      return true;
    }

    let payload = {
      _metadata: {
        createdAt: new Date().toISOString(),
        source: 'AVO_STATION'
      },
      data: value
    };

    // Encriptación condicional (compatible con encryptData de CreateWallet y Login)
    if (encryptionKey) {
      if (typeof encryptionKey !== 'string' || encryptionKey.length < 8) {
        throw new Error('La clave de encriptación debe tener al menos 8 caracteres');
      }

      payload = {
        ...payload,
        _metadata: {
          ...payload._metadata,
          encrypted: true,
          encryptionVersion: '2.0'
        },
        data: await encryptData(value, encryptionKey)
      };
    }

    const serialized = JSON.stringify(payload);
    
    // Control de tamaño
    if (serialized.length > STORAGE_CONFIG.maxSize) {
      throw new Error(`Datos demasiado grandes (${serialized.length} bytes)`);
    }

    localStorage.setItem(storageKey, serialized);
    return true;
  } catch (error) {
    console.error(`Error crítico en saveToStorage (${key}):`, {
      error,
      valueType: typeof value,
      key
    });
    throw new Error(`No se pudo guardar los datos: ${error.message}`);
  }
}

/**
 * Obtiene datos con validación completa
 * Nota: sessionActive se recupera como string ('true'/'false') sin parseo de payload
 * Nota: Compatible con formato legacy (string directo) y nuevo formato (con metadata)
 */
export async function getFromStorage(key, decryptionKey = null) {
  try {
    const storageKey = STORAGE_CONFIG.prefix + key;
    const rawItem = localStorage.getItem(storageKey);

    if (!rawItem) return null;

    // Caso especial para sessionActive: devolver string directamente
    if (key === 'sessionActive') {
      if (rawItem !== 'true' && rawItem !== 'false') {
        throw new Error('Valor de sessionActive inválido');
      }
      return rawItem;
    }

    // Detectar formato legacy (string directo sin estructura JSON de metadata)
    // Para claves como cryptoWallet, recoveryWords que se guardaron con encryptData directo
    if (key === 'cryptoWallet' || key === 'secure_v2_recoveryWords') {
      // Si parece ser un string encriptado directamente (formato legacy)
      if (isLikelyEncrypted(rawItem)) {
        console.log(`Detectado formato legacy para ${key}, devolviendo string encriptado directo`);
        return rawItem; // Devolver el string encriptado tal cual para que Login.js lo desencripte
      }
    }

    // Parseo seguro para nuevo formato
    let parsed;
    try {
      parsed = JSON.parse(rawItem);
    } catch (e) {
      // Si no es JSON, podría ser formato legacy (string directo)
      // Para claves que se guardaron directamente sin estructura
      console.warn(`Datos en ${key} no son JSON, asumiendo formato legacy`);
      return rawItem;
    }

    // Si no tiene metadata, podría ser formato legacy almacenado como JSON simple
    if (!parsed._metadata || !('data' in parsed)) {
      console.warn(`Datos en ${key} sin metadata, asumiendo formato legacy`);
      // Si el objeto parseado parece ser datos directos, devolverlos
      return rawItem;
    }

    // Desencriptación condicional (compatible con decryptData de Login)
    if (parsed._metadata.encrypted) {
      if (!decryptionKey) {
        throw new Error('Se necesita clave de desencriptación');
      }
      
      if (isLikelyEncrypted(parsed.data)) {
        return await decryptData(parsed.data, decryptionKey);
      }
      throw new Error('Formato de datos encriptados no reconocido');
    }

    return parsed.data;
  } catch (error) {
    console.error(`Error en getFromStorage (${key}):`, {
      error,
      decryptionKey: !!decryptionKey
    });
    throw new Error(`No se pudieron recuperar los datos: ${error.message}`);
  }
}

/**
 * Elimina datos con verificación
 */
export function removeFromStorage(key) {
  const storageKey = STORAGE_CONFIG.prefix + key;
  localStorage.removeItem(storageKey);
  
  // Verificación
  if (localStorage.getItem(storageKey)) {
    console.error(`Fallo al eliminar ${storageKey}`);
    return false;
  }
  return true;
}

/**
 * Limpieza segura solo de nuestros datos
 * Nota: Mantiene compatibilidad con la limpieza de sessionActive en Dashboard
 */
export function clearSecureStorage() {
  Object.keys(localStorage)
    .filter(key => key.startsWith(STORAGE_CONFIG.prefix))
    .forEach(key => {
      localStorage.removeItem(key);
      console.log(`Eliminado: ${key}`);
    });
}

/**
 * Verifica la integridad de los datos almacenados
 * Nota: Incluye verificación específica para sessionActive
 */
export function verifyStorageIntegrity() {
  return Object.keys(localStorage)
    .filter(key => key.startsWith(STORAGE_CONFIG.prefix))
    .map(key => {
      try {
        const item = localStorage.getItem(key);
        const bareKey = key.replace(STORAGE_CONFIG.prefix, '');
        if (bareKey === 'sessionActive') {
          if (item !== 'true' && item !== 'false') {
            return { key, status: 'corrupt', error: 'sessionActive debe ser "true" o "false"' };
          }
          return { key, status: 'ok', size: item.length };
        }
        JSON.parse(item);
        return { key, status: 'ok', size: item.length };
      } catch (e) {
        return { key, status: 'corrupt', error: e.message };
      }
    });
}